// The home screen icon (cloud only): which color trameria gets when it is added
// to the home screen. Saved on this device like the theme; choosing points the
// head's `apple-touch-icon` and `manifest` at that color's files.
//
// Promise:
// - It only matters when adding to the home screen. iOS copies the icon at
//   that moment, so an icon already added does not change (remove and add
//   again). Android's Chrome may pick it up when it reads the manifest again.
//   Every color's manifest has the same `id`, so any color is the same app.
// - The links are made anew, not re-pointed: Safari may not read a link whose
//   attribute alone changed.
// - The stored color is applied when this module loads (the app's start), long
//   before anyone can reach "Add to Home Screen".
// - The images and manifests are in `public/` (`/home-icons/<color>-180.png`,
//   `/home-icons/<color>.webmanifest`): the round mark of that color on white,
//   or `<color>-dark` on black (the ground is its own setting). "standard" on
//   white is the page's own (`/apple-touch-icon.png`, `/manifest.webmanifest`).
import { m } from "@trameria/cloud-i18n";
import { defineDeviceSetting, useDeviceSetting } from "@trameria/ui-core";

export const HOME_ICONS = [
  "standard",
  "vermilion",
  "green",
  "purple",
  "ink",
  "white",
  "black",
] as const;
export type HomeIcon = (typeof HOME_ICONS)[number];

const isHomeIcon = (v: string | null): v is HomeIcon =>
  (HOME_ICONS as readonly (string | null)[]).includes(v);

export const homeIconSetting = defineDeviceSetting<HomeIcon>({
  key: "trameria-home-icon",
  parse: (raw) => (isHomeIcon(raw) ? raw : "standard"),
});

/** The ground the round mark sits on, chosen apart from the mark's color. */
export const HOME_GROUNDS = ["white", "black"] as const;
export type HomeGround = (typeof HOME_GROUNDS)[number];

export const homeGroundSetting = defineDeviceSetting<HomeGround>({
  key: "trameria-home-icon-ground",
  parse: (raw) => (raw === "black" ? "black" : "white"),
});

// Bumped whenever the images change: the CDN and iOS keep an image by its URL.
const V = "?v=2";

/** The 180 px image iOS takes; the settings show this same image. */
export const homeIconImage = (id: HomeIcon, ground: HomeGround): string =>
  id === "standard" && ground === "white"
    ? `/apple-touch-icon.png${V}`
    : `/home-icons/${id}${ground === "black" ? "-dark" : ""}-180.png${V}`;

const manifestHref = (id: HomeIcon, ground: HomeGround): string =>
  id === "standard" && ground === "white"
    ? "/manifest.webmanifest"
    : `/home-icons/${id}${ground === "black" ? "-dark" : ""}.webmanifest${V}`;

/** Points the head's links at the color's files (made anew, see above). */
export function applyHomeIcon(id: HomeIcon, ground: HomeGround): void {
  for (const [rel, href] of [
    ["apple-touch-icon", homeIconImage(id, ground)],
    ["manifest", manifestHref(id, ground)],
  ] as const) {
    document.head.querySelector(`link[rel="${rel}"]`)?.remove();
    const link = document.createElement("link");
    link.rel = rel;
    link.href = href;
    document.head.appendChild(link);
  }
}

if (typeof document !== "undefined") {
  const stored = homeIconSetting.read();
  const ground = homeGroundSetting.read();
  if (stored !== "standard" || ground !== "white")
    applyHomeIcon(stored, ground);
}

const LABELS: Record<HomeIcon, () => string> = {
  standard: () => m.web_home_icon_standard(),
  vermilion: () => m.web_home_icon_vermilion(),
  green: () => m.web_home_icon_green(),
  purple: () => m.web_home_icon_purple(),
  ink: () => m.web_home_icon_ink(),
  white: () => m.web_home_icon_white(),
  black: () => m.web_home_icon_black(),
};

const GROUND_LABELS: Record<HomeGround, () => string> = {
  white: () => m.web_home_icon_ground_white(),
  black: () => m.web_home_icon_ground_black(),
};

/** The colors as the images themselves; the chosen one has the chosen ring. */
export function HomeIconChoices() {
  const [icon, setIcon] = useDeviceSetting(homeIconSetting);
  const [ground] = useDeviceSetting(homeGroundSetting);
  return (
    <div className="flex flex-wrap items-center gap-[10px]">
      {HOME_ICONS.map((id) => (
        <button
          key={id}
          type="button"
          aria-pressed={icon === id}
          aria-label={LABELS[id]()}
          onClick={() => {
            setIcon(id);
            applyHomeIcon(id, ground);
          }}
          className={`shrink-0 cursor-pointer rounded-[9px] p-[2px] outline-offset-2 ${
            icon === id ? "ring-2 ring-chosen-fg" : "ring-1 ring-panel-border"
          }`}
        >
          <img
            src={homeIconImage(id, ground)}
            alt=""
            width={28}
            height={28}
            className="block size-7 rounded-[7px]"
          />
        </button>
      ))}
    </div>
  );
}

/** White or black ground, as two buttons under the colors. */
export function HomeGroundChoices() {
  const [icon] = useDeviceSetting(homeIconSetting);
  const [ground, setGround] = useDeviceSetting(homeGroundSetting);
  return (
    <div className="flex items-center gap-2">
      {HOME_GROUNDS.map((g) => (
        <button
          key={g}
          type="button"
          aria-pressed={ground === g}
          onClick={() => {
            setGround(g);
            applyHomeIcon(icon, g);
          }}
          className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[7px] px-2.5 py-1 text-[12px] leading-[1.5] text-fg outline-offset-2 ${
            ground === g ? "ring-2 ring-chosen-fg" : "ring-1 ring-panel-border"
          }`}
        >
          <span
            aria-hidden
            className={`size-3 rounded-full ring-1 ring-panel-border ${
              g === "white" ? "bg-white" : "bg-[#111114]"
            }`}
          />
          {GROUND_LABELS[g]()}
        </button>
      ))}
    </div>
  );
}

/**
 * The row in Appearance, stacked: the name and note above, the seven colors
 * under them (they do not fit the control's column at the right), and the
 * ground under the colors.
 */
export function HomeIconRow() {
  return (
    <div id="home-icon" className="scroll-mt-4 px-4 py-3">
      <div className="text-[13px] leading-[1.5] text-fg">
        {m.web_home_icon()}
      </div>
      <div className="mt-px text-[11.5px] leading-[1.5] text-faint">
        {m.web_home_icon_note()}
      </div>
      <div className="mt-2.5">
        <HomeIconChoices />
      </div>
      <div className="mt-2.5">
        <HomeGroundChoices />
      </div>
    </div>
  );
}

export const homeIconSections = [
  { id: "home-icon", label: () => m.web_home_icon() },
];
